/**
 * Lets a cold Quick Access note route read locally before the startup work that
 * scans every browse card and attachment. The durable inbox still owns routing;
 * this gate only schedules unrelated local work and always has a short escape
 * hatch for an unavailable or quarantined envelope.
 */
export interface InitialWidgetRoutePriorityGate {
  readonly active: boolean
  wait(): Promise<void>
  releaseAfterPaint(): void
  dispose(): void
}

export function createInitialWidgetRoutePriorityGate(
  prioritizeNativeOpenNote: boolean,
  maximumWaitMs: number,
): InitialWidgetRoutePriorityGate {
  const active = prioritizeNativeOpenNote
  if (!active) {
    return {
      active: false,
      wait: () => Promise.resolve(),
      releaseAfterPaint: () => undefined,
      dispose: () => undefined,
    }
  }

  let released = false
  let frameHandle: number | null = null
  let timeoutHandle: ReturnType<typeof globalThis.setTimeout> | null = null
  let release!: () => void
  const ready = new Promise<void>((resolve) => { release = resolve })

  const finish = () => {
    if (released) return
    released = true
    if (timeoutHandle !== null) globalThis.clearTimeout(timeoutHandle)
    timeoutHandle = null
    release()
  }

  return {
    active: true,
    wait: () => new Promise((resolve) => {
      if (released) {
        resolve()
        return
      }
      timeoutHandle = globalThis.setTimeout(finish, maximumWaitMs)
      void ready.then(resolve)
    }),
    releaseAfterPaint: () => {
      if (released || frameHandle !== null) return
      frameHandle = requestAnimationFrame(() => {
        frameHandle = null
        finish()
      })
    },
    dispose: () => {
      if (frameHandle !== null) cancelAnimationFrame(frameHandle)
      frameHandle = null
      finish()
    },
  }
}
